import { useVirtualizer } from "@tanstack/react-virtual";
import { ChevronDown, X } from "lucide-react";
import { type KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface VirtualizedStringComboboxProps {
	options: string[];
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	/** Placeholder pola wyszukiwania w dropdownie. */
	searchPlaceholder?: string;
	disabled?: boolean;
	className?: string;
}

/** Case- i diacritics-insensitive normalizacja do filtrowania ("Neuca" ~ "NEUCA", "łódź" ~ "lodz"). */
function normalizeForSearch(s: string): string {
	return s
		.toLowerCase()
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.replace(/ł/g, "l");
}

/**
 * Buduje unikalne etykiety dla comboboxa (komponent operuje na stringach) —
 * przy duplikacie nazwy dokleja `(#id)`, zeby mapowanie etykieta↔id bylo
 * jednoznaczne.
 */
export function uniqueLabelled<T>(
	items: T[],
	getName: (item: T) => string,
	getId: (item: T) => number,
): { labels: string[]; labelToId: Map<string, number>; idToLabel: Map<number, string> } {
	const counts = new Map<string, number>();
	for (const item of items) {
		const n = getName(item);
		counts.set(n, (counts.get(n) ?? 0) + 1);
	}
	const labelToId = new Map<string, number>();
	const idToLabel = new Map<number, string>();
	for (const item of items) {
		const n = getName(item);
		const id = getId(item);
		const label = (counts.get(n) ?? 0) > 1 ? `${n} (#${id})` : n;
		labelToId.set(label, id);
		idToLabel.set(id, label);
	}
	return { labels: [...labelToId.keys()], labelToId, idToLabel };
}

const ROW_HEIGHT = 32;
const LIST_MAX_HEIGHT = 300;

export function VirtualizedStringCombobox({
	options,
	value,
	onChange,
	placeholder = "Wybierz...",
	searchPlaceholder = "Szukaj...",
	disabled = false,
	className,
}: VirtualizedStringComboboxProps) {
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState("");
	const [highlightedIndex, setHighlightedIndex] = useState(0);
	const searchInputRef = useRef<HTMLInputElement>(null);
	const listRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

	// Wykryj najblizszy Dialog i uzyj go jako Portal container — bez tego FocusScope
	// Dialog'a traktuje Popover (renderowany domyslnie w document.body) jako outside
	// i przejmuje focus z search inputu. Ref: radix-ui/primitives#922
	useEffect(() => {
		if (!open || !triggerRef.current) return;
		const dialog = triggerRef.current.closest<HTMLElement>('[role="dialog"]');
		setPortalContainer(dialog);
	}, [open]);

	const filtered = useMemo(() => {
		if (!search.trim()) return options;
		const q = normalizeForSearch(search);
		return options.filter((o) => normalizeForSearch(o).includes(q));
	}, [options, search]);

	// Reset highlighted przy zmianie filtered (po wyszukiwaniu albo otwarciu)
	// biome-ignore lint/correctness/useExhaustiveDependencies: filtered pochodzi z useMemo i zmienia sie tylko przy search/options
	useEffect(() => {
		setHighlightedIndex(0);
	}, [filtered]);

	const virtualizer = useVirtualizer({
		count: filtered.length,
		getScrollElement: () => listRef.current,
		estimateSize: () => ROW_HEIGHT,
		overscan: 8,
		// initialRect pomaga pre-mount (szczegolnie w testach happy-dom gdzie ResizeObserver nie wyzwala)
		initialRect: { width: 360, height: LIST_MAX_HEIGHT },
	});

	// Force re-measure po otwarciu popovera — w happy-dom ResizeObserver moze nie wyzwolic
	// biome-ignore lint/correctness/useExhaustiveDependencies: virtualizer nie stabilny
	useEffect(() => {
		if (!open) return;
		requestAnimationFrame(() => {
			if (listRef.current) virtualizer.measure();
		});
	}, [open, filtered.length]);

	// Scroll-to-highlighted przy keyboard nav.
	// Virtualizer celowo wykluczony z deps — useVirtualizer nie gwarantuje stabilnej
	// referencji, dodanie go moze powodowac niepotrzebne re-scrolle.
	// biome-ignore lint/correctness/useExhaustiveDependencies: patrz komentarz powyzej
	useEffect(() => {
		if (!open) return;
		if (filtered.length === 0) return;
		virtualizer.scrollToIndex(highlightedIndex, { align: "auto" });
	}, [highlightedIndex, open, filtered.length]);

	// Scroll-to-selected przy otwarciu.
	// biome-ignore lint/correctness/useExhaustiveDependencies: virtualizer nie stabilny, patrz wyzej
	useEffect(() => {
		if (!open || !value) return;
		const idx = filtered.indexOf(value);
		if (idx >= 0) {
			requestAnimationFrame(() =>
				requestAnimationFrame(() => virtualizer.scrollToIndex(idx, { align: "center" })),
			);
			setHighlightedIndex(idx);
		}
	}, [open, value, filtered]);

	const handleSelect = (opt: string) => {
		onChange(opt);
		setOpen(false);
		setSearch("");
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "ArrowDown") {
			e.preventDefault();
			setHighlightedIndex((i) => Math.min(i + 1, filtered.length - 1));
		} else if (e.key === "ArrowUp") {
			e.preventDefault();
			setHighlightedIndex((i) => Math.max(i - 1, 0));
		} else if (e.key === "Enter") {
			e.preventDefault();
			if (filtered[highlightedIndex]) {
				handleSelect(filtered[highlightedIndex]);
			}
		} else if (e.key === "Escape") {
			e.preventDefault();
			setOpen(false);
		}
	};

	return (
		// WYMAGANE: `modal` prop dla Popover wewnatrz Dialog — bez niego Dialog's
		// DismissableLayer traktuje klik w Popover (Portal poza DialogContent) jako
		// "outside click" i zamyka popover. `modal` na Popover tworzy wlasny
		// DismissableLayer z wyzszym priorytetem w stacku, chroniac przed tym.
		// Kontrakt wymaga zeby wszystkie instancje @radix-ui/react-dismissable-layer
		// byly w tej samej wersji (package.json overrides).
		// Ref: https://github.com/radix-ui/primitives/issues/2348
		<Popover open={open} onOpenChange={setOpen} modal>
			<PopoverTrigger asChild>
				<Button
					ref={triggerRef}
					type="button"
					variant="outline"
					disabled={disabled}
					className={cn("w-full justify-between gap-2 font-normal", className)}
				>
					<span className="truncate">{value || placeholder}</span>
					<ChevronDown className="size-3.5 shrink-0 opacity-50" />
				</Button>
			</PopoverTrigger>
			<PopoverContent
				portalContainer={portalContainer}
				className="w-[min(360px,calc(100vw-16px))] p-0 pointer-events-auto"
				align="start"
				onOpenAutoFocus={(e) => {
					// preventDefault blokuje domyslny Radix auto-focus (moze wybrac zly element).
					// Fokusujemy search input SYNCHRONICZNIE (nie setTimeout/rAF) — dzieki temu
					// FocusScope Dialog'a nie ma szansy nadpisac naszego focusu.
					e.preventDefault();
					searchInputRef.current?.focus();
				}}
				onCloseAutoFocus={(e) => e.preventDefault()}
			>
				<div className="p-2 border-b">
					<input
						ref={searchInputRef}
						placeholder={searchPlaceholder}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						onKeyDown={handleKeyDown}
						className="w-full h-8 px-2 text-sm bg-transparent border rounded-md border-input focus:outline-none focus:ring-1 focus:ring-ring"
					/>
				</div>
				{value && (
					<button
						type="button"
						className="w-full text-left px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted/50 flex items-center gap-1.5 border-b"
						onClick={() => handleSelect("")}
					>
						<X className="size-3" />
						Wyczysc
					</button>
				)}
				{filtered.length === 0 ? (
					<div className="px-3 py-4 text-sm text-muted-foreground text-center">Brak wynikow</div>
				) : (
					<div
						ref={listRef}
						style={{ maxHeight: LIST_MAX_HEIGHT, overflow: "auto" }}
						className="overscroll-contain"
						onWheel={(e) => e.stopPropagation()}
						onTouchMove={(e) => e.stopPropagation()}
					>
						<div
							style={{
								height: virtualizer.getTotalSize(),
								width: "100%",
								position: "relative",
							}}
						>
							{virtualizer.getVirtualItems().map((vi) => {
								const opt = filtered[vi.index];
								const isSelected = opt === value;
								const isHighlighted = vi.index === highlightedIndex;
								return (
									<button
										key={opt}
										type="button"
										role="option"
										aria-selected={isSelected}
										data-testid="virtualized-option"
										style={{
											position: "absolute",
											top: 0,
											left: 0,
											width: "100%",
											height: ROW_HEIGHT,
											transform: `translateY(${vi.start}px)`,
										}}
										className={cn(
											"text-left px-3 text-sm hover:bg-muted/50 transition-colors",
											isHighlighted && !isSelected && "bg-muted/30",
											isSelected && "bg-primary/5 !text-primary font-medium",
										)}
										onClick={() => handleSelect(opt)}
										onMouseEnter={() => setHighlightedIndex(vi.index)}
									>
										{opt}
									</button>
								);
							})}
						</div>
					</div>
				)}
			</PopoverContent>
		</Popover>
	);
}
